import type { Country, Zone } from '@seek/audience-zones';
import type { Brand } from '@seek/ca-myprofile-utils';
import type { Language } from '@seek/melways-sites';

import { CREATE_RESUME_PAGE, PROFILE_PAGE } from 'src/components/Router';
import type { ADD, EDIT, DELETE, REJECT } from 'src/consts';

export type AnalyticsBrand = Lowercase<Brand>;

export interface AnalyticsState {
  currentPage?: AnalyticsPage;
  loginId?: string;
  isLoggedIn?: boolean;
  actionOrigin?: AnalyticsActionOrigin;
  profileGuid?: string;
  legacyCandidateId?: string;
  siteCountry?: Lowercase<Country>;
  brand?: AnalyticsBrand;
  zone?: Zone;
  profileVisibilityLevel?: string;
  siteLanguage?: Language;
  experiments?: Array<AnalyticsExperiments | PublicProfileExperiment>;
  hubbleExperiments?: Record<string, HubbleExperiment>;
  profileApproachabilityStatus?: AnalyticsProfileApproachabilityStatus;
  isUnifiedSite?: boolean;
  profileViewedCount?: number;
  profileSearchAppearanceCount?: number;
}

export interface Store {
  getState: () => AnalyticsState;
}

export type ConfirmedActionName = typeof ADD | typeof EDIT | typeof DELETE;
export type UnconfirmedActionName = typeof ADD | typeof EDIT | typeof REJECT;

export type AnalyticsUpdateType = 'add' | 'edit' | 'delete';

export type AnalyticsAddType = 'add' | 'edit';

export type AnalyticsCurrentPageSectionState = 'add' | 'edit' | 'view';

export type AnalyticsProfileApproachabilityStatus = 'on' | 'off';

export type AnalyticsActionOrigin =
  | 'main card'
  | 'resume parsing nudge'
  | 'top nudge'
  | 'application form'
  | 'pop up'
  | 'manage resumes'
  | 'generate resume prompt'
  | 'go1'
  | 'profile'
  | 'centralised nudge';

export type AnalyticsResumeType = 'generated' | 'uploaded';

export interface AnalyticsViewData {
  eventName: 'profile_displayed';
  currentPage: 'profile';
}

export type ManageResumeEventName =
  | 'resume_download_succeeded'
  | 'resume_make_default_succeeded'
  | 'resume_parsing_succeeded'
  | 'resume_upload_pressed'
  | 'profile_update_succeeded';

export interface ManageResumeUpdateProps {
  currentPageSection: 'resume';
  eventName: ManageResumeEventName;
  isResumeDefault?: boolean;
  resumeType?: AnalyticsResumeType;
  updateType?: AnalyticsUpdateType;
  actionOrigin?: AnalyticsActionOrigin;
  linkPosition?: AnalyticsActionOrigin;
  resumeHasSensitivePersonalInfo?: boolean;
  alertShownType?: AnalyticsResumeAlertShownType;
  alertShownTone?: AnalyticsResumeAlertShownTone;
  alertShownPosition?: AnalyticsResumeAlertPosition;
  alertShownTitle?: AnalyticsResumeAlertTitle;
  alertShownMessage?: AnalyticsResumeAlertShownMessage;
  parsedData?: Array<{
    document: string;
    section: string;
    info: string;
    count: number;
  }>;
}

export interface CreateResumeDisplayed {
  currentPage: 'create resume';
  eventName: 'create_resume_displayed';
}

export interface AnalyticsService {
  view: (viewName: AnalyticsViewEvent, data: Record<string, unknown>) => void;
  link: (linkName: AnalyticsLinkEvent, data: Record<string, unknown>) => void;
}

export type VerificationStatus =
  | 'verified'
  | 'not-verified'
  | 'unverifiable'
  | 'none';

export interface AnalyticsLicenceInfo {
  verificationStatus: VerificationStatus;
  isExpired: boolean;
}

export interface AnalyticsQualificationVerifiedInfo {
  qualificationVerificationStatus: VerificationStatus;
}

export const MAIN_PAGE = {
  [PROFILE_PAGE]: 'profile',
  [CREATE_RESUME_PAGE]: 'create resume',
} as const;

export const PAGE = [
  'profile',
  'create resume',
  'profile talent search insights view',
] as const;
export type AnalyticsPage = (typeof PAGE)[number];

export type AnalyticsCurrentPageSection =
  | 'personal details'
  | 'personal summary'
  | 'visibility settings'
  | 'talent search insights'
  | 'career history'
  | 'education'
  | 'licence'
  | 'resume'
  | 'skills'
  | 'language'
  | 'next role'
  | 'verifications'
  | 'references';

export type AnalyticsSuggestionsNudgeType =
  | 'personal_details'
  | 'recent_role'
  | 'profile_visibility'
  | 'resume'
  | 'career_history'
  | 'another_role'
  | 'education'
  | 'salary'
  | 'work_rights'
  | 'skills'
  | 'licence_or_certification'
  | 'language'
  | 'summary'
  | 'availability'
  | 'classification'
  | 'work_preference';

export type AnalyticsSuggestionsCallToActionType =
  | 'add_personal_details'
  | 'add_most_recent_role'
  | 'set_profile_visibility'
  | 'add_resume'
  | 'complete_work_history'
  | 'add_another_role'
  | 'add_education'
  | 'add_salary'
  | 'add_work_rights'
  | 'add_5_or_more_skills'
  | 'add_licence_or_certificate'
  | 'add_language'
  | 'add_summary'
  | 'add_availability'
  | 'add_classification'
  | 'add_work_preference';

// VIEW_EVENT is for events ending in `_displayed` and are always sent with `view` function, and LINK_EVENT is for everything else and always sent with `link` function
export type AnalyticsViewEvent =
  | 'profile_displayed'
  | 'create_resume_displayed'
  | 'profile_section_displayed'
  | 'certsy_dialog_displayed';

export type AnalyticsLinkEvent =
  | 'certsy_dialog_pressed'
  | 'certsy_in_review_pressed'
  | 'certsy_manage_pressed'
  | 'certsy_learn_more_pressed'
  | 'certsy_verify_pressed'
  | 'reverify_pressed'
  | 'company_reviews_pressed'
  | 'create_resume_displayed'
  | 'create_resume_pressed'
  | 'profile_add_country_pressed'
  | 'delete_right_to_work_pressed'
  | 'external_link_pressed'
  | 'navigation_pressed'
  | 'nudge_viewed'
  | 'nudge_pressed'
  | 'nudge_dismiss_pressed'
  | 'opportunity_viewed'
  | 'profile_available_verifications_tab_pressed'
  | 'profile_insights_widget_loaded'
  | 'profile_insights_widget_pressed'
  | 'profile_insights_activity_pressed'
  | 'profile_link_copy_pressed'
  | 'profile_link_open_pressed'
  | 'profile_section_displayed'
  | 'profile_update_succeeded'
  | 'profile_verifications_widget_viewed'
  | 'profile_verifications_widget_pressed'
  | 'profile_your_verifications_tab_pressed'
  | 'resume_delete_pressed'
  | 'resume_download_succeeded'
  | 'resume_make_default_succeeded'
  | 'resume_parsing_new_info_pressed'
  | 'resume_parsing_succeeded'
  | 'resume_upload_pressed'
  | 'alert_viewed'
  | 'sensitive_information_dialog_displayed'
  | 'sensitive_information_dialog_pressed'
  | 'completed_reference_check_pressed'
  | 'profile_public_visibility_pressed';

export type AnalyticsNudgeType =
  | 'default_resume_public_visibility'
  | 'default_resume_standard_visibility'
  | 'default_resume_limited_visibility'
  | 'default_resume_hidden_visibility'
  | 'home_location'
  | 'preferred_location'
  | 'right_to_work';

export type AnalyticsCallToAction =
  | 'set_default_resume'
  | 'set_home_location'
  | 'set_preferred_location'
  | 'set_right_to_work';

export type AnalyticsCallToActionType = 'nudge';

export type AnalyticsCurrentPageSubsection =
  | 'availability'
  | 'work type'
  | 'location'
  | 'right to work'
  | 'salary'
  | 'classification'
  | 'approachability'
  | 'visibility'
  | 'verifications'
  | 'references';

export type AnalyticsCurrencyCode =
  | 'AUD'
  | 'NZD'
  | 'GBP'
  | 'HKD'
  | 'IDR'
  | 'THB';

export type AnalyticsLinkPosition = 'main card' | 'pop up';

export type OpportunityType = 'suggested skills';

export interface AnalyticsHeaderFooterLinkProps {
  linkText: string;
  linkAction?: 'expand' | 'collapse';
  linkPosition?: 'header' | 'footer' | 'expanded menu';
}

export type AnalyticsCountryName = 'main card' | 'pop up';

export interface AnalyticsExperiments {
  id: string;
  num: number;
  variant?: '0' | '1' | '2';
}

export interface HubbleExperiment {
  name: string;
  participantStatus: 'enrolled';
  variant: string;
}

export interface PublicProfileExperiment {
  id: string;
  variant:
    | 'Ineligible'
    | 'Treatment 1'
    | 'Treatment 2'
    | 'Treatment 3'
    | 'Treatment 4'
    | 'Treatment 5'
    | 'Treatment 6';
}

export interface AnalyticsProfileInsightsProps {
  currentPage?: AnalyticsPage;
  currentPageSection?: AnalyticsCurrentPageSection;
  currentPageSectionState?: AnalyticsCurrentPageSectionState;
}
export interface AnalyticsProfileVisibilityProps {
  currentPage?: AnalyticsPage;
  currentPageSection?: AnalyticsCurrentPageSection;
  currentPageSectionState?: AnalyticsCurrentPageSectionState;
}

export interface AnalyticsProfileSuggestionsProps {
  currentPage?: AnalyticsPage;
  nudgeType?: AnalyticsSuggestionsNudgeType | string;
  nudgeCount?: number;
  callToActionType?: AnalyticsCallToActionType;
  callToAction?: AnalyticsSuggestionsCallToActionType;
  nudgePosition?: number;
}

export type AnalyticsVerifiableCredentialType =
  | 'identity'
  | 'workplace'
  | 'licence'
  | 'education'
  | 'right_to_work';

export type AnalyticsVerificationsWidgetName =
  | 'verifications'
  | 'identity'
  | 'workplace';

export type AnalyticsVerificationsWidgetCallToAction =
  | 'view_verifications'
  | 'add_verifications'
  | 'add_verification_workplace'
  | 'add_verification_identity';

export type AnalyticsVerificationsWidgetLinkText =
  | 'view all'
  | 'get verified'
  | 'verify your workplace'
  | 'verify your identity';

export type AnalyticsVerificationsCurrentPageSubsection =
  | 'your verifications'
  | 'available verifications';

export type AnalyticsReferencesCallToAction =
  | 'continue to seek pass'
  | 'learn more';

export type AnalyticsSensitiveInformationLinkText =
  'Sensitive information: what you need to know';

export type AnalyticsResumeAlertShownType = 'notice';

export type AnalyticsResumeAlertShownTone = 'caution' | 'critical';

export type AnalyticsResumeAlertPosition = 'content';

export type AnalyticsResumeAlertTitle =
  | 'resume with sensitive information'
  | 'resume with virus detected';

export type AnalyticsResumeAlertShownMessage =
  | 'May contain sensitive information.'
  | 'Virus detected. Resume cant be used.';
